import { getPersonCropRectFromFaceData } from '../lib/faceDetection/personCropFromFace.js';

// ─── Constants ────────────────────────────────────────────────────────────────

/** 16:9→9:16 双人：脸宽/高均小于此归一化阈值时，两人合一个裁切框（否则仍上下分屏） */
const DUAL_TINY_FACE_MAX_169_916 = 0.08;

/** 16:9→9:16 单人「小脸」分支：脸宽/高均小于此值时走上 fit + 下放大居中人物 */
const SINGLE_169_TINY_FACE_MAX = 0.2;

/** 单人小脸分屏额外条件：人脸中心横向落在片源左/右各该比例宽度内（归一化 0~1） */
const SINGLE_169_TINY_SPLIT_LATERAL_BAND = 0.2;

/** 单人小脸分屏额外条件：人脸中心纵向落在片源上/下各该比例高度内（归一化 0~1） */
const SINGLE_169_TINY_SPLIT_VERTICAL_BAND = 0.25;
/** 16:9→9:16 单人小脸下半屏：人物取景默认缩放；`clampSingle169BottomCropScale` 入参非法时回退此值 */
const SINGLE_169_BOTTOM_PERSON_ZOOM = 0.5;

/** 双人竖拼：`personRegionZoom` > 1 时求交前外扩倍率的夹紧范围 */
const DUAL_SPLIT_PERSON_REGION_SCALE_MIN = 0.48;
const DUAL_SPLIT_PERSON_REGION_SCALE_MAX = 3.2;

/** 求交后再绕人脸缩放的归一化框最小边长，避免除零或过碎 */
const DUAL_SPLIT_POST_ZOOM_MIN_SIDE = 0.028;

/** `personRegionZoom` 在 (0,1) 时求交后倍率的夹紧范围（过小易糊） */
const DUAL_SPLIT_POST_ZOOM_FACTOR_MIN = 0.48;
const DUAL_SPLIT_POST_ZOOM_FACTOR_MAX = 2.8;

/**
 * 双人竖拼 `computeDualVerticalSplitPersonNormRects` 的默认 options（可与调用方对象合并覆盖）。
 *
 * | 字段 | 含义 | 建议 / 取值范围 |
 * |------|------|----------------|
 * | `personRegionZoom` | **(0,1)** 求交后绕脸**缩小**取景（人像变大）；**(1,+∞)** 求交前**放大**人物框（人像变小）；**1** 默认。大于 1 时倍率夹 **[0.48, 3.2]**；小于 1 时求交后因子夹 **[0.48, 1)**。 |
 * | `widthMultiplier` | 人物框宽度 ≈ 人脸宽 × 该值。典型 **2.8～5**。 |
 * | `heightMultiplier` | 人物框高度 ≈ 人脸高 × 该值。典型 **4～6**。 |
 * | `centerShiftYInFaceHeights` | 裁切框中心相对人脸中心**下移**（× 人脸高）。典型 **0.4～0.65**。 |
 * | `headClearanceInFaceHeights` | 人脸顶沿上方至少留白（× 人脸高）。典型 **0.35～0.6**。 |
 * | `smallFaceTightenMinScale` | 小人脸时取景收紧系数下限。**0.45～1**。 |
 * | `faceDestXRatio` | cover 时人脸中心落在输出宽度的比例（内部夹 **[0.08, 0.92]**）。 |
 * | `faceDestYRatio` | 人脸中心落在输出高度的比例（内部夹 **[0.08, 0.5]**）。 |
 */
const DUAL_SPLIT_DEFAULT_PERSON_OPTS = Object.freeze({
    personRegionZoom: 1,
    widthMultiplier: 3.85,
    heightMultiplier: 5.1,
    centerShiftYInFaceHeights: 0.5,
    headClearanceInFaceHeights: 0.52,
    smallFaceTightenMinScale: 0.78,
    faceDestXRatio: 0.5,
    faceDestYRatio: 1 / 3,
});

// ─── Utility object ───────────────────────────────────────────────────────────

const FJFaceUtil = {
    // ─── Exposed constants ─────────────────────────────────────────────────────
    DUAL_TINY_FACE_MAX_169_916,
    SINGLE_169_TINY_FACE_MAX,
    SINGLE_169_TINY_SPLIT_LATERAL_BAND,
    SINGLE_169_BOTTOM_PERSON_ZOOM,
    DUAL_SPLIT_DEFAULT_PERSON_OPTS,
    DUAL_SPLIT_PERSON_REGION_SCALE_MIN,
    DUAL_SPLIT_PERSON_REGION_SCALE_MAX,
    DUAL_SPLIT_POST_ZOOM_MIN_SIDE,
    DUAL_SPLIT_POST_ZOOM_FACTOR_MIN,
    DUAL_SPLIT_POST_ZOOM_FACTOR_MAX,

    // ─── Dual-split options normalisation ──────────────────────────────────────

    /**
     * 将用户 options 规范为含 `personRegionZoom` 的对象；若未传 zoom 则用旧字段 `personRegionExpandScale`×`personRegionShrinkScale` 推导。
     * @param {Record<string, unknown>} [userOptions]
     */
    normalizeDualSplitDrawOptions(userOptions = {}) {
        let z = userOptions.personRegionZoom;
        if (z == null || !Number.isFinite(Number(z)) || Number(z) <= 0) {
            const e = Number(userOptions.personRegionExpandScale);
            const s = Number(userOptions.personRegionShrinkScale);
            const pe = Number.isFinite(e) && e > 0 ? e : 1;
            const ps = Number.isFinite(s) && s > 0 ? s : 1;
            z = pe * ps;
        }
        const personRegionZoom = Math.max(1e-6, Number(z));
        const merged = { ...DUAL_SPLIT_DEFAULT_PERSON_OPTS, ...userOptions, personRegionZoom };
        delete merged.personRegionExpandScale;
        delete merged.personRegionShrinkScale;
        delete merged.personRegionScale;
        return merged;
    },

    /**
     * 求交**之前**：`personRegionZoom` 仅当 **> 1** 时参与外扩，并夹紧到 `[MIN, MAX]`；否则返回 1。
     * @param {{ personRegionZoom?: number }} opt 须已 `normalizeDualSplitDrawOptions`
     */
    resolveDualSplitPersonRegionScale(opt) {
        const z = Number(opt.personRegionZoom);
        if (!Number.isFinite(z) || z <= 1) return 1;
        return Math.max(DUAL_SPLIT_PERSON_REGION_SCALE_MIN, Math.min(DUAL_SPLIT_PERSON_REGION_SCALE_MAX, z));
    },

    /**
     * 求交**之后**绕脸缩放因子：仅当 `personRegionZoom < 1` 时返回 zoom（夹紧），否则 1。
     * @param {{ personRegionZoom?: number }} opt 须已 normalize
     */
    getDualSplitPostZoomFactor(opt) {
        const z = Number(opt.personRegionZoom);
        if (!Number.isFinite(z) || z >= 1 - 1e-9) return 1;
        return Math.max(DUAL_SPLIT_POST_ZOOM_FACTOR_MIN, Math.min(1 - 1e-9, z));
    },

    /**
     * 与 clip 相交后的归一化取景框，再绕人脸中心等比缩放，并再次与 clip 相交。
     */
    applyDualSplitPostZoomNormRect(norm, faceData, clipNorm, factor) {
        if (!norm || !faceData || !clipNorm) return norm;
        const raw = Number(factor);
        if (!Number.isFinite(raw) || raw <= 0 || Math.abs(raw - 1) < 1e-6) return norm;
        const s = Math.max(DUAL_SPLIT_POST_ZOOM_FACTOR_MIN, Math.min(DUAL_SPLIT_POST_ZOOM_FACTOR_MAX, raw));
        const ax = this.faceCenterX(faceData);
        const ay = this.faceCenterY(faceData);
        let w = Math.max(DUAL_SPLIT_POST_ZOOM_MIN_SIDE, Math.min(1, norm.width * s));
        let h = Math.max(DUAL_SPLIT_POST_ZOOM_MIN_SIDE, Math.min(1, norm.height * s));
        const x = Math.max(0, Math.min(1 - w, ax - w / 2));
        const y = Math.max(0, Math.min(1 - h, ay - h / 2));
        const candidate = { x, y, width: w, height: h };
        const hit = this.intersectNormRects(candidate, clipNorm);
        if (
            hit &&
            hit.width >= DUAL_SPLIT_POST_ZOOM_MIN_SIDE * 0.85 &&
            hit.height >= DUAL_SPLIT_POST_ZOOM_MIN_SIDE * 0.85
        ) {
            return hit;
        }
        return norm;
    },

    // ─── Face geometry helpers ─────────────────────────────────────────────────

    faceCenterX(faceData) {
        return faceData.x + faceData.width / 2;
    },

    faceCenterY(faceData) {
        return faceData.y + faceData.height / 2;
    },

    /** 当前帧仅一条轨迹且为主体（U²-Net）回退 bbox。 */
    isSubjectOnlySegment(segmentsMerged) {
        return segmentsMerged?.length === 1 && !!segmentsMerged[0]?.faceData?.isSubject;
    },

    /**
     * 主体跨画幅裁切：横源→竖出以主体水平中心裁竖条（高拉满）；竖源→横出以主体垂直中心裁横条（宽拉满）。
     * @returns {{ x: number, y: number, width: number, height: number } | null}
     */
    computeSubjectCrossAspectSourceCropPx(faceData, fw, fh, inputAr, outputAr) {
        if (!faceData || fw <= 0 || fh <= 0 || inputAr <= 0 || outputAr <= 0) {
            return null;
        }
        const cx = this.faceCenterX(faceData) * fw;
        const cy = this.faceCenterY(faceData) * fh;

        if (inputAr > 1 && outputAr < 1) {
            const cropH = fh;
            const cropW = Math.min(fw, cropH * outputAr);
            return {
                x: Math.max(0, Math.min(fw - cropW, cx - cropW / 2)),
                y: 0,
                width: cropW,
                height: cropH,
            };
        }
        if (inputAr < 1 && outputAr > 1) {
            const cropW = fw;
            const cropH = Math.min(fh, cropW / outputAr);
            return {
                x: 0,
                y: Math.max(0, Math.min(fh - cropH, cy - cropH / 2)),
                width: cropW,
                height: cropH,
            };
        }
        return null;
    },

    /** 是否适用主体跨画幅裁切（横↔竖且仅一条 isSubject 轨迹）。 */
    shouldUseSubjectCrossAspectRender(inputAr, outputAr, segmentsMerged) {
        if (!this.isSubjectOnlySegment(segmentsMerged)) {
            return false;
        }
        return (inputAr > 1 && outputAr < 1) || (inputAr < 1 && outputAr > 1);
    },

    /**
     * 人脸中心横向是否落在片源左右边缘带内（左带 [0,band]、右带 [1-band,1]）。
     * @param {object} [faceData]
     * @param {number} [band] 默认 {@link SINGLE_169_TINY_SPLIT_LATERAL_BAND}
     */
    faceCenterInLateralBands(
        faceData,
        bandX = SINGLE_169_TINY_SPLIT_LATERAL_BAND,
        bandY = SINGLE_169_TINY_SPLIT_VERTICAL_BAND,
    ) {
        if (!faceData || !Number.isFinite(bandX)) return false;
        const cx = this.faceCenterX(faceData);
        const cy = this.faceCenterY(faceData);
        if (!Number.isFinite(cx) || !Number.isFinite(cy)) return false;
        return (cx <= bandX || cx >= 1 - bandX) && (cy <= bandY || cy >= 1 - bandY);
    },

    /** 人脸轨迹签名（顺序敏感），用于转场等。 */
    getSegmentsTrackSignature(segments) {
        if (!Array.isArray(segments) || segments.length === 0) return '';
        return segments
            .map(seg => {
                if (seg?.trackId != null) return String(seg.trackId);
                const fd = seg?.faceData;
                if (!fd) return '?';
                return `p:${Math.round(this.faceCenterX(fd) * 1000)}_${Math.round(this.faceCenterY(fd) * 1000)}`;
            })
            .join('|');
    },

    /**
     * 同一时刻的人脸轨迹：时间块互不重叠时通常只命中一块；仍遍历全部块以兼容旧数据并按 trackId 去重。
     * @param {unknown[]} faceDetectionBlocks
     * @param {number} sourceTime
     * @returns {{ faceData: object, trackId?: number }[]}
     */
    getAllFaceSegmentsAtSourceTime(faceDetectionBlocks, sourceTime) {
        if (!Array.isArray(faceDetectionBlocks) || faceDetectionBlocks.length === 0) return [];
        const eps = 1e-3;
        const seen = new Set();
        const out = [];
        for (const block of faceDetectionBlocks) {
            if (sourceTime + eps < block.startTime || sourceTime - eps >= block.endTime) continue;
            const segs = block.segments;
            if (!Array.isArray(segs)) continue;
            for (const seg of segs) {
                if (!seg?.faceData) continue;
                const tid = seg.trackId;
                const key =
                    tid != null
                        ? `t:${tid}`
                        : `p:${Math.round(this.faceCenterX(seg.faceData) * 1000)}_${Math.round(this.faceCenterY(seg.faceData) * 1000)}`;
                if (seen.has(key)) continue;
                seen.add(key);
                out.push(seg);
            }
        }
        return out;
    },

    bothFacesTinyFor169Dual(twoSegments, maxSize = DUAL_TINY_FACE_MAX_169_916) {
        return twoSegments.every(s => {
            const f = s?.faceData;
            return f && f.width < maxSize;
        });
    },

    // ─── Aspect ratio helpers ──────────────────────────────────────────────────

    approxEqualAspectRatio(a, b, relTol = 0.02) {
        if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return false;
        return Math.abs(a - b) <= relTol * Math.min(a, b);
    },

    isOutputAspect916(outputAr, relTol = 0.04) {
        if (!Number.isFinite(outputAr) || outputAr <= 0) return false;
        const target = 9 / 16;
        return Math.abs(outputAr - target) <= relTol * target;
    },

    isInputAspect169(inputAr, relTol = 0.04) {
        if (!Number.isFinite(inputAr) || inputAr <= 0) return false;
        const target = 16 / 9;
        return Math.abs(inputAr - target) <= relTol * target;
    },

    isSpecial169To916Pair(inputAr, outputAr) {
        return this.isInputAspect169(inputAr) && this.isOutputAspect916(outputAr);
    },

    // ─── Normalised rect utilities ─────────────────────────────────────────────

    /** 归一化矩形 → 像素源矩形（sx/sy/sw/sh） */
    normRectToPixels(norm, fw, fh) {
        return {
            sx: norm.x * fw,
            sy: norm.y * fh,
            sw: norm.width * fw,
            sh: norm.height * fh,
        };
    },

    pixelsRectToNorm01(r, W, H) {
        if (!r || W <= 0 || H <= 0) return { x: 0, y: 0, width: 0, height: 0 };
        return { x: r.x / W, y: r.y / H, width: r.width / W, height: r.height / H };
    },

    clampNormRect01(r) {
        const width = Math.max(0, Math.min(1, r.width));
        const height = Math.max(0, Math.min(1, r.height));
        const x = Math.max(0, Math.min(1 - width, r.x));
        const y = Math.max(0, Math.min(1 - height, r.y));
        return { x, y, width, height };
    },

    roundNormRect01(r, decimals = 4) {
        const f = 10 ** decimals;
        const q = v => Math.round(v * f) / f;
        return this.clampNormRect01({ x: q(r.x), y: q(r.y), width: q(r.width), height: q(r.height) });
    },

    /**
     * 归一化矩形相交（0~1 坐标系）
     * @returns {{ x: number, y: number, width: number, height: number } | null}
     */
    intersectNormRects(a, b) {
        if (!a || !b) return null;
        const x1 = Math.max(a.x, b.x);
        const y1 = Math.max(a.y, b.y);
        const x2 = Math.min(a.x + a.width, b.x + b.width);
        const y2 = Math.min(a.y + a.height, b.y + b.height);
        const width = x2 - x1;
        const height = y2 - y1;
        if (width < 1e-4 || height < 1e-4) return null;
        return { x: x1, y: y1, width, height };
    },

    /**
     * 将归一化裁切框以中心为锚整体缩放（用于可调「拉远/拉近」），再夹到 [0,1]²。
     * @param {number} scale 建议 ≥0.85，>1 放大取景区
     */
    expandNormRectAboutCenter(norm, scale) {
        if (!norm || !Number.isFinite(scale) || scale <= 0) return norm;
        const cx = norm.x + norm.width / 2;
        const cy = norm.y + norm.height / 2;
        const w = Math.min(Math.max(norm.width * scale, 1e-6), 1);
        const h = Math.min(Math.max(norm.height * scale, 1e-6), 1);
        const x = Math.max(0, Math.min(1 - w, cx - w / 2));
        const y = Math.max(0, Math.min(1 - h, cy - h / 2));
        return { x, y, width: w, height: h };
    },

    /**
     * 解码帧位图尺寸（VideoFrame / ImageBitmap / HTMLCanvasElement）
     * @param {CanvasImageSource} frame
     */
    getFrameBitmapSize(frame) {
        if (!frame) return { w: 0, h: 0 };
        if (typeof frame.codedWidth === 'number' && frame.codedWidth > 0)
            return { w: frame.codedWidth, h: frame.codedHeight };
        if (typeof frame.width === 'number' && frame.width > 0) return { w: frame.width, h: frame.height };
        if (typeof frame.videoWidth === 'number' && frame.videoWidth > 0)
            return { w: frame.videoWidth, h: frame.videoHeight };
        return { w: 0, h: 0 };
    },

    /** 清晰层相对输出矩形：等比 **contain**（完整显示、可留黑边） */
    getDrawRect(originRect, outputRect) {
        const { width: vw, height: vh } = originRect;
        const { width: outW, height: outH } = outputRect;
        const scale = Math.min(outW / vw, outH / vh);
        const drawW = vw * scale;
        const drawH = vh * scale;
        return {
            dx: (outW - drawW) / 2,
            dy: (outH - drawH) / 2,
            drawW,
            drawH,
            sx: 0,
            sy: 0,
            sw: vw,
            sh: vh,
        };
    },

    // ─── Cover crop computation (unified) ──────────────────────────────────────

    /**
     * 计算 cover 裁切窗：从源矩形 (sx,sy,sw,sh) 中选取铺满目标 (dW,dH) 所需的最小矩形。
     *
     * - 不传 `focus`（或 null）：**中心对齐**裁切。
     * - 传 `focus`：使源上 (focus.x, focus.y) 落在目标的
     *   (focus.destXRatio × dW, focus.destYRatio × dH) 处，再夹紧在裁切窗内。
     *
     * @param {number} sx @param {number} sy @param {number} sw @param {number} sh 源矩形（像素）
     * @param {number} dW @param {number} dH 目标尺寸（像素）
     * @param {{ x: number, y: number, destXRatio?: number, destYRatio?: number } | null} [focus]
     *   焦点像素坐标及其在目标区的落点比例；`destXRatio` 夹 [0.08,0.92]，`destYRatio` 夹 [0.08,0.5]
     * @returns {{ csx: number, csy: number, cropW: number, cropH: number } | null}
     */
    computeCoverClipPixels(sx, sy, sw, sh, dW, dH, focus = null) {
        const scale = Math.max(dW / sw, dH / sh);
        const cropW = Math.min(dW / scale, sw);
        const cropH = Math.min(dH / scale, sh);

        let csx, csy;
        if (focus && Number.isFinite(focus.x) && Number.isFinite(focus.y)) {
            const rx = Math.max(0.08, Math.min(0.92, focus.destXRatio ?? 0.5));
            const ry = Math.max(0.08, Math.min(0.5, focus.destYRatio ?? 1 / 3));
            csx = focus.x - rx * cropW;
            csy = focus.y - ry * cropH;
        } else {
            csx = sx + (sw - cropW) / 2;
            csy = sy + (sh - cropH) / 2;
        }
        csx = Math.max(sx, Math.min(sx + sw - cropW, csx));
        csy = Math.max(sy, Math.min(sy + sh - cropH, csy));
        return { csx, csy, cropW, cropH };
    },

    // ─── Drawing utilities ─────────────────────────────────────────────────────

    /**
     * Cover 绘制：等比铺满目标区 (dx,dy,dW,dH)。
     *
     * - 不传 `focus`（或 null）：**中心对齐**。
     * - 传 `focus`：焦点锚定，见 {@link computeCoverClipPixels}。
     *
     * @param {{ x: number, y: number, destXRatio?: number, destYRatio?: number } | null} [focus]
     */
    drawImageCover(ctx, frame, sx, sy, sw, sh, dx, dy, dW, dH, focus = null) {
        const clip = this.computeCoverClipPixels(sx, sy, sw, sh, dW, dH, focus);
        if (!clip) return false;
        ctx.drawImage(frame, clip.csx, clip.csy, clip.cropW, clip.cropH, dx, dy, dW, dH);
        return true;
    },

    /** Fit（contain）绘制：等比缩放到目标区内，不裁切，可留黑边。 */
    drawImageFit(ctx, frame, sx, sy, sw, sh, dx, dy, dW, dH) {
        if (sw <= 0 || sh <= 0 || dW <= 0 || dH <= 0) return false;
        const scale = Math.min(dW / sw, dH / sh);
        const drawW = sw * scale;
        const drawH = sh * scale;
        ctx.drawImage(frame, sx, sy, sw, sh, dx + (dW - drawW) / 2, dy + (dH - drawH) / 2, drawW, drawH);
        return true;
    },

    /** 按归一化矩形 norm 从帧裁切后 fit（contain）到整个画布。 */
    drawPersonCropFitCanvas(ctx, frame, norm, fw, fh, canvasW, canvasH) {
        const { sx, sy, sw, sh } = this.normRectToPixels(norm, fw, fh);
        if (sw <= 1 || sh <= 1) return false;
        return this.drawImageFit(ctx, frame, sx, sy, sw, sh, 0, 0, canvasW, canvasH);
    },

    // ─── Single-169 split layout ───────────────────────────────────────────────

    /** 单人 169 小脸分屏：上下各占画布高度约一半。 */
    splitSingle169TinyHalfScreenHeights(canvasW, canvasH) {
        if (canvasW <= 0 || canvasH <= 0) return { topH: 0, bottomH: Math.max(0, canvasH) };
        let topH = Math.floor(canvasH / 2);
        let bottomH = canvasH - topH;
        if (bottomH < 2) {
            bottomH = 2;
            topH = Math.max(0, canvasH - bottomH);
        }
        if (topH < 1) return { topH: 0, bottomH: canvasH };
        return { topH, bottomH };
    },

    /**
     * 顶区：片源上按 `cw:topH` 比例中心裁切，用于 `drawImage` 到 `(0,0,cw,topH)`。
     * @returns {{ sx: number, sy: number, sw: number, sh: number } | null}
     */
    compute169SingleTinyTopCenterCropSourcePx(fw, fh, cw, topH) {
        if (fw <= 0 || fh <= 0 || cw <= 0 || topH <= 0) return null;
        const slotAr = cw / topH;
        const srcAr = fw / fh;
        let sx, sy, sw, sh;
        if (srcAr > slotAr) {
            sh = fh;
            sw = fh * slotAr;
            sx = (fw - sw) / 2;
            sy = 0;
        } else {
            sw = fw;
            sh = fw / slotAr;
            sx = 0;
            sy = (fh - sh) / 2;
        }
        if (sw <= 1 || sh <= 1) return null;
        return { sx, sy, sw, sh };
    },

    clampSingle169BottomCropScale(v) {
        const n = Number(v);
        if (!Number.isFinite(n)) return SINGLE_169_BOTTOM_PERSON_ZOOM;
        return Math.max(0.12, Math.min(1.25, n));
    },

    /**
     * 人物取景归一化矩形按 `zoom` 缩放，锚点为人脸中心，保持宽高比并夹入 [0,1]²。
     */
    personCropNormZoomedAboutFaceCenter(faceData, aspectHalf, sourceAr, zoom) {
        const norm = getPersonCropRectFromFaceData(faceData, {
            aspectRatio: aspectHalf,
            sourceAspectRatio: sourceAr,
        });
        if (!faceData || !Number.isFinite(zoom) || zoom <= 0) return norm;
        let w = norm.width * zoom;
        let h = norm.height * zoom;
        if (w <= 0 || h <= 0) return norm;
        const fit = Math.min(1, 1 / w, 1 / h);
        w *= fit;
        h *= fit;
        const cx = this.faceCenterX(faceData);
        const cy = this.faceCenterY(faceData);
        const x = Math.max(0, Math.min(1 - w, cx - w / 2));
        const y = Math.max(0, Math.min(1 - h, cy - h / 2));
        return { x, y, width: w, height: h };
    },

    // ─── Dual-split layout ─────────────────────────────────────────────────────

    /**
     * 判断两人脸是左右并排还是上下分布，返回各自 clip 与排序后的 topSeg/botSeg。
     */
    dualPortraitClipsAndOrder(twoSegments) {
        const [s0, s1] = twoSegments;
        const cx0 = this.faceCenterX(s0.faceData);
        const cy0 = this.faceCenterY(s0.faceData);
        const cx1 = this.faceCenterX(s1.faceData);
        const cy1 = this.faceCenterY(s1.faceData);
        const yTol = 0.065;
        const dx = Math.abs(cx0 - cx1);
        const dy = Math.abs(cy0 - cy1);
        // 左右并排：纵差小 OR 横距不小于纵距（访谈 16:9 左右双人常见，避免把略高低的双人误判为上下分屏）
        const sideBySide = dy < yTol || dx + 1e-6 >= dy;
        const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

        if (sideBySide) {
            const topSeg = cx0 <= cx1 ? s0 : s1;
            const botSeg = cx0 <= cx1 ? s1 : s0;
            let midX = clamp((this.faceCenterX(topSeg.faceData) + this.faceCenterX(botSeg.faceData)) / 2, 0.08, 0.92);
            return {
                topSeg,
                botSeg,
                clipTop: { x: 0, y: 0, width: midX, height: 1 },
                clipBot: { x: midX, y: 0, width: 1 - midX, height: 1 },
            };
        }

        const topSeg = cy0 <= cy1 ? s0 : s1;
        const botSeg = cy0 <= cy1 ? s1 : s0;
        let midY = clamp((this.faceCenterY(topSeg.faceData) + this.faceCenterY(botSeg.faceData)) / 2, 0.08, 0.92);
        return {
            topSeg,
            botSeg,
            clipTop: { x: 0, y: 0, width: 1, height: midY },
            clipBot: { x: 0, y: midY, width: 1, height: 1 - midY },
        };
    },

    /**
     * 计算双人竖拼上下两半的人物归一化取景与焦点像素（供绘制或时间线采样复用）。
     *
     * @param {{ faceData: object }[]} twoSegments
     * @returns {{
     *   halfH: number,
     *   normTop: object, normBot: object,
     *   topSeg: object, botSeg: object,
     *   faceDestXRatio: number, faceDestYRatio: number,
     *   fxt: number, fyt: number, fxb: number, fyb: number,
     * } | null}
     */
    computeDualVerticalSplitPersonNormRects(twoSegments, fw, fh, canvasW, canvasH, options = {}) {
        const halfH = canvasH / 2;
        if (halfH < 2) return null;
        const aspectHalf = canvasW / halfH;
        const { topSeg, botSeg, clipTop, clipBot } = this.dualPortraitClipsAndOrder(twoSegments);
        const opt = this.normalizeDualSplitDrawOptions(options);
        const personRegionPreExpandScale = this.resolveDualSplitPersonRegionScale(opt);
        const postZoomFactor = this.getDualSplitPostZoomFactor(opt);
        const {
            widthMultiplier,
            heightMultiplier,
            centerShiftYInFaceHeights,
            headClearanceInFaceHeights,
            smallFaceTightenMinScale,
            faceDestXRatio,
            faceDestYRatio,
        } = opt;

        const personOpts = {
            aspectRatio: aspectHalf,
            sourceAspectRatio: fw / fh,
            widthMultiplier,
            heightMultiplier,
            centerShiftYInFaceHeights,
            headClearanceInFaceHeights,
            smallFaceTightenMinScale,
        };

        const faceFocusPixels = fd => ({ fx: this.faceCenterX(fd) * fw, fy: this.faceCenterY(fd) * fh });

        const buildHalfNorm = (seg, clipNorm) => {
            const raw = getPersonCropRectFromFaceData(seg.faceData, personOpts);
            const scaled = this.expandNormRectAboutCenter(raw, personRegionPreExpandScale);
            const merged = this.intersectNormRects(scaled, clipNorm) || this.intersectNormRects(raw, clipNorm) || raw;
            return this.applyDualSplitPostZoomNormRect(merged, seg.faceData, clipNorm, postZoomFactor);
        };

        try {
            const normTop = buildHalfNorm(topSeg, clipTop);
            const normBot = buildHalfNorm(botSeg, clipBot);
            const pt = this.normRectToPixels(normTop, fw, fh);
            const pb = this.normRectToPixels(normBot, fw, fh);
            if (pt.sw <= 1 || pt.sh <= 1 || pb.sw <= 1 || pb.sh <= 1) return null;
            const { fx: fxt, fy: fyt } = faceFocusPixels(topSeg.faceData);
            const { fx: fxb, fy: fyb } = faceFocusPixels(botSeg.faceData);
            return { halfH, normTop, normBot, topSeg, botSeg, faceDestXRatio, faceDestYRatio, fxt, fyt, fxb, fyb };
        } catch {
            return null;
        }
    },

    // ─── Render profile resolution ─────────────────────────────────────────────

    /**
     * 根据输入/输出比例与当前帧人脸数量，决定渲染分支：
     * - `subject`：仅主体 bbox 且横↔竖跨画幅
     * - `169_dual_tiny` / `169_dual_split`：16:9↔9:16 且双人
     * - `169_single_tiny_split`：16:9↔9:16 且单人小脸且在边缘带内
     * - `single`：单人普通
     * - `blur_fit`：零人脸或多人非特判
     */
    resolveFaceRenderProfile(inputAr, outputAr, segmentsMerged) {
        const n = segmentsMerged.length;
        if (this.shouldUseSubjectCrossAspectRender(inputAr, outputAr, segmentsMerged)) {
            return 'subject';
        }
        const special169_916 = this.isSpecial169To916Pair(inputAr, outputAr);
        if (n === 2 && special169_916) {
            return this.bothFacesTinyFor169Dual(segmentsMerged) ? '169_dual_tiny' : '169_dual_split';
        }
        // if (
        //     n === 1 &&
        //     special169_916 &&
        //     this.bothFacesTinyFor169Dual(segmentsMerged, SINGLE_169_TINY_FACE_MAX) &&
        //     this.faceCenterInLateralBands(segmentsMerged[0]?.faceData)
        // ) {
        //     return '169_single_tiny_split';
        // }
        if (n === 1) return 'single';
        return 'blur_fit';
    },
};

export default FJFaceUtil;
